import { useMemo } from "react";
import { ChipPicker, type ChipPickerChip, type PickerOption } from "@cntrl/ui";
import { useReleases } from "../../app/hooks/useReleases.js";
import { ReleaseArtwork } from "./ReleaseArtwork.js";
import { linkPickerOrder, releaseTypeLabel } from "./releaseFormat.js";
import { formatDropDate } from "../../app/hooks/useTasks.js";
import styles from "./ReleasePicker.module.scss";

export interface ReleasePickerProps {
  /** The linked release, by id. `null` is "standalone", which is a real answer and not an empty field. */
  value: string | null;
  onChange: (releaseId: string | null) => void;
  label?: string;
  placeholder?: string;
}

/**
 * The RELEASE link picker — the crew picker's sibling, drawing from the catalogue instead of the band team
 * (operator request, 2026-08-23). A task could only ever be attached to a release by creating it from that
 * release's To-do; created anywhere else — the dashboard, which is where most tasks start — there was no
 * field to say which rollout it belonged to, and no way to say so afterwards.
 *
 * Single-select, so it rides `ChipPicker` at `max={1}`: pick a release and the type-ahead is replaced by the
 * chosen chip, whose × puts the field back. That is the whole difference from the crew picker, which is why
 * this is a second thin wrapper over the shared shell rather than a second copy of it.
 *
 * **Ordering is {@link linkPickerOrder}, not the catalogue's** — unscheduled first, then dates descending
 * from the furthest out. See that helper for why a picker wants the opposite order to a browsing grid.
 *
 * **Artwork is the identifier here, not decoration.** Bands ship singles with near-identical titles and the
 * type-ahead is filtering on title alone; the cover is what makes a row scannable. It resolves through
 * `ReleaseArtwork`, so a release with no cover gets its deterministic identity gradient rather than a
 * fictional one, and the band's own upload always outranks a vendor hotlink.
 *
 * Requires `releases:read` — the CALLER gates on it. A seat without it (the touring crew) has no catalogue
 * to offer and must not see the field at all, which is a decision about the surrounding form, not this
 * control (rendering a permanently-empty picker would read as "you have no releases").
 */
export function ReleasePicker({
  value,
  onChange,
  label = "Release (Optional)",
  placeholder = "Search your releases…",
}: ReleasePickerProps) {
  const releases = useReleases();

  // Cancelled rollouts are not link targets — a task hung off one is work nobody is going to do. They stay
  // readable on a task that already points at one (the drawer resolves the title from the id either way).
  const items = useMemo(
    () => linkPickerOrder((releases.data?.items ?? []).filter((r) => r.status !== "cancelled")),
    [releases.data],
  );
  const chosen = useMemo(() => items.find((r) => r.releaseId === value), [items, value]);

  const options: PickerOption[] = useMemo(
    () =>
      items
        .filter((r) => r.releaseId !== value)
        .map((r) => ({
          value: r.releaseId!,
          // PLAIN TEXT: what the type-ahead filters on and what AT announces. The title alone — a person
          // searching types the title, never "Single" or a date.
          label: r.title ?? "Untitled release",
          render: (
            <span className={styles.option}>
              <ReleaseArtwork seed={r.releaseId} size="chip" mediaId={r.coverMediaId} imageUrl={r.coverImageUrl} />
              <span className={styles.optionText}>
                <span className={styles.optionTitle}>{r.title ?? "Untitled release"}</span>
                <span className={styles.optionMeta}>
                  {releaseTypeLabel(r.releaseType)}
                  {" · "}
                  {dropLabel(r.releaseDate)}
                </span>
              </span>
            </span>
          ),
        })),
    [items, value],
  );

  // The chosen chip is TEXT + the drop date, exactly as the crew picker's chips are text: the cover belongs
  // to the list, where it does the scanning work. (It is also the only shape available — `Tag` renders its
  // children inside a `<span>`, and `ReleaseArtwork` is a `<div>`; nesting flow content inside phrasing
  // content is invalid. Putting a cover here needs the artwork component to take its element, not a fork.)
  //
  // A chosen release that is not in the loaded page still shows as a chip — honestly labelled — rather than
  // silently clearing the caller's value.
  const chips: ChipPickerChip[] = value
    ? [
        {
          value,
          label: chosen?.title ?? "Linked release",
          meta: chosen ? dropLabel(chosen.releaseDate) : undefined,
        },
      ]
    : [];

  return (
    <ChipPicker
      label={label}
      placeholder={placeholder}
      loading={releases.isLoading}
      loadingLabel="Loading releases…"
      options={options}
      chips={chips}
      chipSize="md"
      max={1}
      onAdd={onChange}
      onRemove={() => onChange(null)}
    />
  );
}

/** The drop date, or the honest "no date" — `formatDropDate` returns an EMPTY STRING for an undated
 *  release, which would render as a bare separator rather than telling anyone anything. */
function dropLabel(date: string | null | undefined): string {
  return formatDropDate(date) || "No date";
}
